import { useMediaUpload } from '@shared/hooks/useMediaUpload';
import { MediaFile, MediaType } from '@shared/types/media.types';
import { useCallback, useState } from 'react';
import { toast } from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import { MediaPreview } from '../components/MessageMediaPreview';

const getErrorMessage = (error: unknown, fileType: string): string => {
  if (error instanceof Error) {
    if (error.message.includes('size')) {
      return `The ${fileType} file is too large. Maximum size allowed is 20MB`;
    }
    if (error.message.includes('type')) {
      return `Invalid ${fileType} format. Please use a supported format`;
    }
    if (error.message.includes('network')) {
      return 'Network error. Please check your connection and try again';
    }
    return error.message;
  }
  return `Failed to upload ${fileType}. Please try again`;
};

export const useMessageMedia = () => {
  const [mediaPreview, setMediaPreview] = useState<MediaPreview | null>(null);
  const { uploadMedia } = useMediaUpload();

  const handleFileSelect = useCallback(
    async (file: File) => {
      const id = uuidv4();
      const type = file.type.startsWith('image/')
        ? MediaType.IMAGE
        : MediaType.VIDEO;
      const fileType = type === MediaType.IMAGE ? 'image' : 'video';

      const preview: MediaPreview = {
        id,
        type,
        url: URL.createObjectURL(file),
        progress: 0,
      };

      setMediaPreview(preview);

      try {
        const mediaFile: MediaFile = {
          file,
          preview: preview.url,
          type,
          progress: 0,
        };

        const uploadedUrl = await uploadMedia(mediaFile);
        setMediaPreview((prev) =>
          prev?.id === id ? { ...prev, url: uploadedUrl, progress: 100 } : prev
        );

        return { id, type, url: uploadedUrl };
      } catch (error) {
        const errorMessage = getErrorMessage(error, fileType);
        toast.error(errorMessage);
        setMediaPreview(null);
        throw error;
      }
    },
    [uploadMedia]
  );

  const handleGifSelect = useCallback((gifUrl: string, message?: string) => {
    const id = uuidv4();
    setMediaPreview({
      id,
      type: 'gif',
      url: gifUrl,
      message,
    });
    return { id, type: 'gif' as const, url: gifUrl, message };
  }, []);

  const removeMedia = useCallback(() => {
    if (mediaPreview?.url && !mediaPreview.url.startsWith('http')) {
      URL.revokeObjectURL(mediaPreview.url);
    }
    setMediaPreview(null);
  }, [mediaPreview]);

  return {
    mediaPreview,
    handleFileSelect,
    handleGifSelect,
    removeMedia,
  };
};
